import dayjs from "dayjs";
import { useCallback, useEffect, useState } from 'react';
import { useFetch } from '../common/useFetch';
import { useEmitMessage } from '../common/useEmitMessage';
import { SetSubscription, SubscriptionState, Subscription } from '../App/useSubscription';
import { formatDeliveryDateFromDayjs } from '../../utils/format';

export const useSubscriptionSkip = (
  subscription: SubscriptionState,
  setSubscription: SetSubscription,
  token: string,
  successCallback?: () => void
): UseSubscriptionSkip => {

  // スキップかスキップキャンセルか
  const [isAllowSkipCancel, setIsAllowSkipCancel] = useState(false);
  useEffect(() => {
    if (!subscription || !subscription.subscriptionHistories) return;

    const skippedCount = subscription?.subscriptionHistories.filter(h => {
      return h.status === 'SKIPPED' && !h.canceledAt;
    })[0]?.skipCount || 0;

    // 次回注文作成日
    const rewindNextBillingDate = dayjs(subscription.nextBillingDate).add(
      subscription.billingPolicyIntervalCount * -skippedCount,
      subscription.billingPolicyInterval.toLowerCase() as 'day' | 'week' | 'month' | 'year'
    );

    // 次回お届け日
    const rewindNextDeliveryDate = rewindNextBillingDate.add(subscription.deliveryDays, 'day');

    // スキップの取り消しは履歴がある＆戻った注文作成日が過去にならないこと
    setIsAllowSkipCancel(skippedCount > 0 && rewindNextBillingDate.isAfter(new Date()));
  }, [subscription]);

  // API通信メッセージ関連
  const emitMessage = useEmitMessage();
  const failed = () => emitMessage('app-error', 'keep');
  const success = () => {
    if (successCallback) successCallback();
    emitMessage('app-modify-skip');
  }

  // キャンセル関連
  const [skipCancelSubscriptionState, callSkipCancelSubscription] 
    = useFetch<Subscription>('skipCancelSubscription', token, success, failed);

  const skipCancelSubscription = useCallback((deliveryDate: Date) => {

    if (!subscription) return;

    const params = {
      subscriptionId: subscription.subscriptionContractId,
      skipCancelDate: formatDeliveryDateFromDayjs(deliveryDate)
    };

    emitMessage('app-updating', 'keep');
    callSkipCancelSubscription(params);
  }, [subscription?.subscriptionContractId]);

  useEffect(() => {
    if (!skipCancelSubscriptionState.data) return;
    setSubscription({ 
      ...subscription!,
      nextBillingDate: new Date(skipCancelSubscriptionState.data.nextBillingDate),
      subscriptionHistories: skipCancelSubscriptionState.data.subscriptionHistories
    })
  }, [skipCancelSubscriptionState.data])

  // スキップ関連
  const [skipSubscriptionState, callSkipSubscription] 
    = useFetch<Subscription>('skipSubscription', token, success, failed);

  const skipSubscription = useCallback((deliveryDate: Date) => {

    if (!subscription) return;

    const params = {
      subscriptionId: subscription.subscriptionContractId,
      skipCount: 1, // 一回で固定している、複数回スキップする場合は要調整
      skipDate: formatDeliveryDateFromDayjs(deliveryDate)
    };

    emitMessage('app-updating', 'keep');
    callSkipSubscription(params);
  }, [subscription?.subscriptionContractId]);

  useEffect(() => {
    if (!skipSubscriptionState.data) return;
    setSubscription({ 
      ...subscription!,
      nextBillingDate: new Date(skipSubscriptionState.data.nextBillingDate),
      subscriptionHistories: skipSubscriptionState.data.subscriptionHistories
    })
  }, [skipSubscriptionState.data]);

  return [
    {
      isAllowSkipCancel,
      loadingSkipCancelSubscription: skipCancelSubscriptionState.loading,
      loadingSkipSubscription: skipSubscriptionState.loading,
    },
    {
      skipSubscription,
      skipCancelSubscription
    }
  ];
}

type UseSubscriptionSkip =  [
  {
    loadingSkipCancelSubscription: boolean,
    loadingSkipSubscription: boolean,
    isAllowSkipCancel: boolean
  },
  {
    skipCancelSubscription: (deliveryDate: Date) => void
    skipSubscription: (deliveryDate: Date) => void 
  }
];